import { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Chip,
  Tooltip,
  Box,
  Alert,
  Button,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import FlagIcon from "@mui/icons-material/Flag";
import { useAppSelector, useAppDispatch } from "../../../../redux/store";
import {
  fetchBillingAddresses,
  fixCountryCodes,
  type BillingAddressEntry,
  type FixCountryCodesResult,
} from "../../../../redux/slices/customersSlice";
import Table from "../../../../components/Table";
import BillingAddressEditDialog from "./BillingAddressEditDialog";
import type { Customer } from "../../../../types/api";
import { COUNTRY_NAMES } from "../../../../constants/address";

interface BillingAddressesDialogProps {
  open: boolean;
  onClose: () => void;
}

const BillingAddressesDialog = ({
  open,
  onClose,
}: BillingAddressesDialogProps) => {
  const dispatch = useAppDispatch();
  const {
    billingAddresses,
    billingAddressesStatus,
    billingAddressesError,
  } = useAppSelector((state) => state.customers);
  const updateBillingStatus = useAppSelector(
    (state) => (state as any).projects?.updateProjectBillingAddressStatus,
  );

  const [fixing, setFixing] = useState(false);
  const [fixResult, setFixResult] = useState<FixCountryCodesResult | null>(null);

  useEffect(() => {
    if (open && billingAddressesStatus === "idle") {
      dispatch(fetchBillingAddresses());
    }
  }, [open, billingAddressesStatus, dispatch]);

  // Refetch billing addresses after a successful update
  useEffect(() => {
    if (updateBillingStatus === "succeeded") {
      dispatch(fetchBillingAddresses());
    }
  }, [updateBillingStatus, dispatch]);

  const handleFixCountryCodes = useCallback(async () => {
    setFixing(true);
    setFixResult(null);
    try {
      const result = await dispatch(fixCountryCodes()).unwrap();
      setFixResult(result);
      dispatch(fetchBillingAddresses());
    } catch {
      setFixResult({ total: 0, fixed: 0, skipped: 0, errors: -1 });
    } finally {
      setFixing(false);
    }
  }, [dispatch]);
  const [editEntry, setEditEntry] = useState<BillingAddressEntry | null>(null);

  const editCustomer: Customer | null = editEntry
    ? ({
        id: editEntry.customerId,
        providerId: editEntry.customerId,
        name: editEntry.customerName,
        email: "",
        timeCreated: "",
        timeUpdated: "",
        status: "active",
        billingAddress: {
          billAddress1: editEntry.billAddress1,
          billAddress2: editEntry.billAddress2 || "",
          billAddressCity: editEntry.billAddressCity,
          billAddressState: editEntry.billAddressState,
          billAddressZip: editEntry.billAddressZip,
          billAddressCountry: editEntry.billAddressCountry,
        },
      } as Customer)
    : null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 0, height: "80vh" },
        }}
      >
        <DialogTitle
          component="div"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid",
            borderColor: "divider",
            pb: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Customer Billing Addresses
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              color="warning"
              startIcon={fixing ? <CircularProgress size={16} color="inherit" /> : <FlagIcon />}
              onClick={handleFixCountryCodes}
              disabled={fixing}
              sx={{ borderRadius: 0, }}
            >
              {fixing ? "Fixing..." : "Fix country codes"}
            </Button>
            {billingAddressesStatus === "succeeded" && (
              <Typography variant="body2" color="text.secondary">
                {billingAddresses.length} address{billingAddresses.length !== 1 ? "es" : ""}
              </Typography>
            )}
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, "&:first-of-type": { pt: 0 } }}>
          {billingAddressesStatus === "failed" && (
            <Alert severity="error" sx={{ m: 2 }}>
              {billingAddressesError || `Failed to load billing addresses from Bill.com`}
            </Alert>
          )}
          <Table
            enableColumnSearch
            loading={billingAddressesStatus === "loading"}
            headers={[
              { key: "customerName", displayName: "Customer" },
              {
                key: "billAddress1",
                displayName: "Street Address",
                minWidth: 220,
                format: ({ row }: any) => {
                  if (!row.billAddress1) return "—";
                  return row.billAddress2
                    ? `${row.billAddress1}, ${row.billAddress2}`
                    : row.billAddress1;
                },
                searchValue: ({ row }: any) =>
                  `${row.billAddress1 || ""} ${row.billAddress2 || ""}`,
              },
              {
                key: "billAddressCity",
                displayName: "City",
                format: ({ row }: any) => row.billAddressCity || "—",
              },
              {
                key: "billAddressState",
                displayName: "State",
                format: ({ row }: any) => row.billAddressState || "—",
              },
              {
                key: "billAddressZip",
                displayName: "ZIP",
                format: ({ row }: any) => row.billAddressZip || "—",
              },
              {
                key: "billAddressCountry",
                displayName: "Country",
                format: ({ row }: any) => {
                  const code = row.billAddressCountry;
                  if (!code) return "—";
                  const isKnown = !!COUNTRY_NAMES[code];
                  return (
                    <Chip
                      label={code}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderRadius: 0,
                        fontWeight: 600,
                        fontFamily: "monospace",
                        backgroundColor: isKnown
                          ? "rgba(76, 175, 80, 0.12)"
                          : "rgba(244, 67, 54, 0.12)",
                        borderColor: isKnown ? "#059669" : "#dc2626",
                        color: isKnown ? "#059669" : "#dc2626",
                      }}
                    />
                  );
                },
              },
              {
                key: "_actions",
                displayName: "",
                disableColumnSearch: true,
                format: ({ row }: any) => (
                  <Tooltip title="Edit billing address">
                    <IconButton
                      size="small"
                      onClick={() => setEditEntry(row as BillingAddressEntry)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ),
              },
            ]}
            rows={billingAddresses}
            rowKey="customerId"
            defaultSortKey="customerName"
            defaultRowsPerPage={-1}
          />
        </DialogContent>
      </Dialog>

      <Snackbar
        open={!!fixResult}
        autoHideDuration={6000}
        onClose={() => setFixResult(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setFixResult(null)}
          severity={fixResult?.errors ? "warning" : "success"}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {fixResult?.errors === -1
            ? "Failed to fix country codes."
            : `Fixed ${fixResult?.fixed} addresses, skipped ${fixResult?.skipped}${fixResult?.errors ? `, ${fixResult.errors} errors.` : "."}`
          }
        </Alert>
      </Snackbar>

      <BillingAddressEditDialog
        open={!!editEntry}
        customer={editCustomer}
        onClose={() => setEditEntry(null)}
      />
    </>
  );
};

export default BillingAddressesDialog;
